// ── My journal: a tech's own week, on My Workbench (Tech Mode part 2, 2 Oct) ────
//
// Four answers for the person at the stand:
//   - how much have I logged this week, and today
//   - what is waiting for me that I have not started (assigned in Lightspeed)
//   - what did I start and leave paused, with Resume
//   - what did I work on each day, and what happened to those tickets
//
// Time and events come from /api/effort/journal (Spinboard's own records).
// Customer and bike come from the ticket lists My Workbench already loaded,
// so this costs no Lightspeed calls. A ticket that has since left both lists
// shows its number alone.

import React, { useState, useEffect, useCallback } from 'react';
import { API, authHeaders } from '../utils/helpers';
import { fmtDur, fmtTime, fmtWhen, dayHeading, eventLabel, colors, Panel, IdTag, Chip, StatTile, Row, Empty } from './effortUi';

const fmtDue = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

export default function WorkJournal({ T, isDark, myName, tickets, finishedTickets, board, onOpenTicket }) {
  const c = colors(isDark);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);       // ticket id being resumed

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/effort/journal?days=7`, { credentials: 'include', headers: authHeaders() });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Could not load your journal.');
      setData(d); setError('');
    } catch (err) { setError(err.message); }
  }, []);
  // Once a minute, so a running timer's hours keep up.
  useEffect(() => { load(); const id = setInterval(load, 60000); return () => clearInterval(id); }, [load]);

  const byId = {};
  for (const t of [...(finishedTickets || []), ...(tickets || [])]) byId[String(t.id)] = t;
  const ticketFor = (id) => byId[String(id)] || { id: String(id), displayId: `SVC-${id}`, customer: '', bike: '', status: '' };
  const isOpenTicket = (id) => (tickets || []).some(t => String(t.id) === String(id));
  const doneOnBoard = (id) => !!(board && board.done && board.done[String(id)]);

  const resume = async (id) => {
    setBusy(id); setError('');
    try {
      const r = await fetch(`${API}/api/timer/ticket/${encodeURIComponent(id)}/start`, { method: 'POST', credentials: 'include', headers: authHeaders(true) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'The timer did not start.');
      onOpenTicket(ticketFor(id));
    } catch (err) { setError(err.message); }
    setBusy(null);
  };

  if (!data) {
    // In a panel, so it reads on a background picture too.
    return <Panel T={T}><div style={{ color: T.textSub, fontSize: 14, padding: 24, textAlign: 'center' }} role={error ? 'alert' : undefined}>{error || 'Loading your journal…'}</div></Panel>;
  }

  const todaySecs = (data.worked.find(d => d.date === data.today) || {}).seconds || 0;
  const running = data.inProgress.find(p => p.running);
  const started = new Set(data.inProgress.map(p => String(p.workorderId)));
  const paused = data.inProgress.filter(p => isOpenTicket(p.workorderId) && !doneOnBoard(p.workorderId))
    .sort((a, b) => (b.running ? 1 : 0) - (a.running ? 1 : 0) || new Date(b.lastAt) - new Date(a.lastAt));

  // Up next: assigned to me in Lightspeed, still on my plate, not started.
  // Due date first (past due at the top), then oldest drop-off.
  const upNext = !myName ? [] : (tickets || [])
    .filter(t => t.tech === myName && !doneOnBoard(t.id) && !started.has(String(t.id)))
    .sort((a, b) => (a.etaOut ? new Date(a.etaOut) : Infinity) - (b.etaOut ? new Date(b.etaOut) : Infinity)
      || new Date(a.timeIn || 0) - new Date(b.timeIn || 0));
  const UP_NEXT_MAX = 6;

  const dueChip = (t) => {
    if (!t.etaOut) return null;
    if (t.isOverdue) return <Chip isDark={isDark} dot="#e74c3c">Past due {fmtDue(t.etaOut)}</Chip>;
    if (t.daysUntilDue === 0) return <Chip isDark={isDark} dot="#e67e22">Due today</Chip>;
    return <Chip isDark={isDark}>Due {fmtDue(t.etaOut)}</Chip>;
  };
  const who = (t) => (
    <>
      <span style={{ fontWeight: 700, fontSize: 15, color: T.text }}>{t.customer || 'Ticket'}</span>
      {t.bike && t.bike !== '—' && <span style={{ fontSize: 13, color: c.sub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{t.bike}</span>}
    </>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {error && <div role="alert" style={{ fontSize: 14, color: '#e74c3c' }}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <StatTile T={T} label="This week" value={fmtDur(data.week.seconds)} sub="logged since Monday" />
        <StatTile T={T} label="Tickets" value={data.week.tickets} sub="worked on this week" />
        <StatTile T={T} label="Checklists" value={data.week.checklists} sub="completed this week" />
        <StatTile T={T} label="Today" value={fmtDur(todaySecs)} valueColor={running ? c.good : undefined}
          sub={running ? `timer running on SVC-${running.workorderId}` : 'no timer running'} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 16, alignItems: 'start' }}>
        <Panel T={T} title="Up next · assigned to me" right={upNext.length > UP_NEXT_MAX ? `${upNext.length} in all` : null}>
          {!myName ? <Empty T={T}>Your login has no name on it, so Spinboard cannot tell which tickets are yours.</Empty>
            : upNext.length === 0 ? <Empty T={T}>Nothing assigned to you that you have not started.</Empty>
            : upNext.slice(0, UP_NEXT_MAX).map(t => (
              <Row key={t.id} T={T} isDark={isDark} onOpen={() => onOpenTicket(t)} title="Open on the bench">
                <IdTag id={t.id} />{who(t)}
                <span style={{ marginLeft: 'auto' }}>{dueChip(t)}</span>
              </Row>
            ))}
        </Panel>

        <Panel T={T} title="In progress · paused timers">
          {paused.length === 0 ? <Empty T={T}>Nothing paused. Tickets you pause or that stop automatically wait here.</Empty>
            : paused.map(p => {
              const t = ticketFor(p.workorderId);
              const when = p.running ? <span style={{ fontSize: 13, color: c.good, fontWeight: 600 }}>Timer running</span>
                : p.lastReason === 'auto' ? <span style={{ fontSize: 13, color: c.warn }}>stopped automatically, {fmtWhen(p.lastAt)}</span>
                : <span style={{ fontSize: 13, color: c.sub }}>{p.lastReason === 'switch' ? 'switched away' : 'paused'} {new Date(p.lastAt).toDateString() === new Date().toDateString() ? fmtTime(p.lastAt) : fmtWhen(p.lastAt)}</span>;
              return (
                <Row key={p.workorderId} T={T} isDark={isDark} onOpen={() => onOpenTicket(t)} title="Open on the bench">
                  <IdTag id={p.workorderId} />
                  <span style={{ fontWeight: 700, fontSize: 15, color: T.text }}>{t.customer || 'Ticket'}</span>
                  {when}
                  <span style={{ marginLeft: 'auto', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: T.text }}>{fmtDur(p.seconds)}</span>
                  {!p.running && (
                    <button type="button" disabled={busy === p.workorderId}
                      onClick={e => { e.stopPropagation(); resume(p.workorderId); }}
                      style={{ border: 'none', borderRadius: 8, background: '#27ae60', color: '#fff', fontWeight: 700, fontSize: 13,
                        padding: '8px 12px', fontFamily: 'inherit', cursor: 'pointer', minHeight: 36, opacity: busy === p.workorderId ? 0.7 : 1 }}>
                      {busy === p.workorderId ? 'Starting…' : '▶ Resume'}
                    </button>
                  )}
                </Row>
              );
            })}
        </Panel>
      </div>

      <Panel T={T} title="What I worked on" right="last 7 days">
        {data.worked.length === 0 ? <Empty T={T}>No time logged in the last 7 days.</Empty>
          : data.worked.map(day => (
            <div key={day.date} style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 6 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, borderBottom: `1px solid ${T.cardBorder}`, paddingBottom: 6 }}>
                <span style={{ fontWeight: 700, fontSize: 15, color: T.text }}>{dayHeading(day.date, data.today)}</span>
                <span style={{ fontSize: 13, color: c.sub }}>{fmtDur(day.seconds)} logged</span>
              </div>
              {day.tickets.map(w => {
                const t = ticketFor(w.workorderId);
                return (
                  <div key={w.workorderId} role="button" tabIndex={0} title="Open on the bench"
                    onClick={() => onOpenTicket(t)} onKeyDown={e => { if (e.key === 'Enter') onOpenTicket(t); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 4px', cursor: 'pointer', flexWrap: 'wrap', borderRadius: 8 }}>
                    <IdTag id={w.workorderId} />
                    <span style={{ fontWeight: 600, fontSize: 14, minWidth: 140, color: T.text }}>{t.customer || ''}</span>
                    <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', minWidth: 64, color: T.text }}>{w.seconds ? fmtDur(w.seconds) : '—'}</span>
                    <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {w.events.map((e, i) => (
                        <Chip key={i} isDark={isDark} color={e.kind === 'sendback' ? c.bad : undefined}>{eventLabel(e)}</Chip>
                      ))}
                    </span>
                  </div>
                );
              })}
            </div>
          ))}
      </Panel>
    </div>
  );
}
